import React, { useState } from 'react';
import { GoogleGenAI } from "@google/genai";
import { Sparkles, Send, Loader2, BrainCircuit, Rocket } from 'lucide-react';
import Button from './Button';

const AICareerScout: React.FC = () => {
  const [input, setInput] = useState('');
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleScout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    setIsLoading(true);
    try {
      // Fix: Direct use of process.env.API_KEY as per Google GenAI SDK guidelines
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `I am a teenager interested in: "${input}". 
                  Based on the FuturePath Teens mission of helping teenagers discover their future paths, 
                  suggest 3 potential career paths that match my interests. 
                  Keep the advice inspiring, modern, and easy to understand. 
                  Use markdown formatting with bold career titles and brief bullet points for why they fit.`,
        config: {
          systemInstruction: "You are a career counselor for teenagers. Your tone is encouraging, modern, and tech-savvy. You help students connect their hobbies and interests to high-demand future careers.",
        }
      });
      
      setSuggestion(response.text || "I couldn't find a path right now. Try describing your hobbies or favorite school subjects!");
    } catch (error) {
      console.error("AI Scouting error:", error);
      setSuggestion("The Scout is currently recharging. Please try again in a moment!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto my-12 p-1 rounded-3xl bg-gradient-to-tr from-brand-cyan/30 via-brand-border to-brand-green/30">
      <div className="bg-brand-dark rounded-[calc(1.5rem-2px)] p-6 md:p-10 relative overflow-hidden">
        {/* Background Accents */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-cyan/10 blur-3xl -mr-10 -mt-10" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-brand-green/10 blur-3xl -ml-10 -mb-10" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-brand-cyan/20 text-brand-cyan">
              <BrainCircuit className="w-6 h-6" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-brand-white">AI Career Scout</h2>
          </div>

          <p className="text-brand-grey2 mb-8 max-w-2xl">
            Not sure where to start? Tell the Scout about your hobbies, favorite subjects, or what you're good at, and we'll help you find a path.
          </p>

          <form onSubmit={handleScout} className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="flex-grow relative">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ex: I love gaming and drawing, and I'm good at math..."
                className="w-full bg-brand-bg/50 border border-brand-border rounded-xl px-4 py-3 text-brand-white focus:outline-none focus:border-brand-cyan transition-colors"
                disabled={isLoading}
              />
            </div>
            <Button 
              type="submit" 
              disabled={isLoading || !input.trim()}
              className="md:w-auto"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Scout Paths
                </>
              )}
            </Button>
          </form>

          {suggestion && (
            <div className="animate-fade-in-up bg-brand-bg/40 border border-brand-cyan/20 rounded-2xl p-6 text-brand-grey1 leading-relaxed">
              <div className="flex items-center gap-2 mb-4 text-brand-cyan font-semibold text-sm uppercase tracking-wider">
                <rocket className="w-4 h-4" />
                Scout Recommendations
              </div>
              <div className="prose prose-invert max-w-none space-y-4 whitespace-pre-wrap">
                {suggestion}
              </div>
              <div className="mt-6 pt-6 border-t border-brand-border/50">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {setSuggestion(null); setInput('');}}
                  className="text-xs"
                >
                  Start New Session
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AICareerScout;
